import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useClientNoteEditPageRedirect } from '@pages/clients/note/helpers.ts';
import { useViewClientNotePageRedirect } from '@pages/clients/note/view-note-page/helpers.ts';
import { EditAndDeleteDropdown } from '@widgets/shared/EditAndDeleteDropdown/index.ts';
import { NoteBody, useRemoveNote } from '@features/clients';
import { getInitialM } from '@shared/helpers/index.ts';
import { useIsAuthor } from '@shared/hooks/useIsAuthor.ts';
import { TextOverflow } from '@shared/ui/TextOverflow/index.tsx';
import type { Note as Notemodel } from 'library/models/clients';
import * as st from './styles.module.scss';

interface Props {
  note: Notemodel;
}

export const Note: React.FC<Props> = ({ note }) => {
  const { id: noteId, content, author } = note;
  const { id: authorId, last_name, middle_name, first_name } = author;

  const { removeNote } = useRemoveNote({ id: noteId });
  const { redirectOnEditNote } = useClientNoteEditPageRedirect();
  const { redirectOnClientNotePage } = useViewClientNotePageRedirect();

  const { isAuthor } = useIsAuthor({ authorId });

  const handleClickNote = () => {
    redirectOnClientNotePage(`note/view/${noteId}`);
  };

  const authorMonogram = useMemo(() => {
    return getInitialM(first_name, middle_name, last_name);
  }, [first_name, last_name, middle_name]);

  return (
    <div className={cn(st.note, isAuthor && st.isAuthor)} onClick={handleClickNote}>
      <span className={st.date}>{format(parseISO(note.created), 'dd.MM.yyyy')}</span>
      <NoteBody noteDescription={content} withAuthorName={!isAuthor} />
      {isAuthor ? (
        <EditAndDeleteDropdown
          smallButton={true}
          onDelete={() => {
            removeNote();
          }}
          onEdit={() => {
            redirectOnEditNote(`note/edit/${noteId}`);
          }}
        />
      ) : (
        <TextOverflow className={st.author}>{authorMonogram}</TextOverflow>
      )}
    </div>
  );
};
